<template>
  <div>
    <b-row>
      <b-col>
        <b-card no-body>
          <b-card-header>
            <b-col
              lg="2"
              class="my-1"
            >
              <b-form-group
                class="mb-0"
              >
                <label class="d-inline-block text-sm-left mr-50">Per page</label>
                <b-form-select
                  id="perPageSelect"
                  v-model="perPage"
                  size="sm"
                  :options="pageOptions"
                  class="w-50"
                />
              </b-form-group>
            </b-col>

            <b-col
              lg="6"
              class="my-1"
            >
              <b-form-group
                label="Filter"
                label-cols-sm="1"
                label-align-sm="right"
                label-size="sm"
                label-for="filterInput"
                class="mb-0"
              >
                <b-input-group size="sm">
                  <b-form-input
                    id="filterInput"
                    v-model="filter"
                    type="search"
                    placeholder="Type to Search"
                  />
                  <b-input-group-append>
                    <b-button
                      :disabled="!filter"
                      @click="filter = ''"
                    >
                      Clear
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <Can
              i="manage"
              a="object"
            >
              <b-col
                label-cols-sm="2"
                class="my-1"
              >
                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  v-b-modal.modal-center
                  variant="outline-primary"
                  block
                  @click="$router.push('/Objects/addObject')"
                >
                  + Add Object
                </b-button>
              </b-col>
            </Can>
          </b-card-header>

          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :current-page="currentPage"
            :items="objects"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            :filter="filter"
            :filter-included-fields="filterOn"
            show-empty
            empty-text="No objects found."
            :busy="isBusy"

            @filtered="onFiltered"
            @row-clicked="onRowClick"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong> Loading...</strong>
              </div>
            </template>

            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
            </template>

            <template #cell(site)="data">
              <div v-if="sitesDict[data.item.siteObjectsId]">
                <b-badge
                  variant="warning"
                  @click="$router.push(`/site/${data.item.siteObjectsId}`)"
                >
                  {{ sitesDict[data.item.siteObjectsId].name }}
                </b-badge>
              </div>
              <div v-else>
                <span class="text-muted">No Site</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <b-button-group>
                <b-button
                  variant="flat-primary"
                  class="btn-icon"
                  @click="showEditModal(data)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  variant="flat-primary"
                  class="btn-icon"
                  @click="showDeleteModal(data)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </b-button-group>
            </template>
            <template #cell(created)="data">
              {{ new Date(`${data.item.createdAt}`).toLocaleString() }}
            </template>
          </b-table>
          <b-col
            class="mb-2"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
          </b-col>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      id="editObjectModal"
      ref="editObjectModal"
      title="Edit Object"
      ok-title="Save"
      cancel-variant="outline-primary"
      ok-variant="primary"
      @ok="updateObject"
      @cancel="cancelEditObjectModal"
    >
      <b-row>
        <b-col>
          <b-form-group
            label="Object Name"
            label-for="objectName"
          >
            <b-form-input
              v-if="'name' in object"
              id="objectName"
              ref="objectNameInput"
              v-model="object.name"
              required
              placeholder="Enter object name"
              :state="object.name.length > 0"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Serial Number"
            label-for="serialNumber"
          >
            <b-form-input
              id="serialNumber"
              v-model="object.manufacturerSerialNumber"

              placeholder="Enter serial number"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Manufacturer"
            label-for="manufacturer"
          >
            <b-form-input
              id="manufacturer"
              v-model="object.manufacturer"
              placeholder="Enter manufacturer"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Model"
            label-for="model"
          >
            <b-form-input
              id="model"
              v-model="object.model"
              placeholder="Enter model"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Model Number"
            label-for="modelNumber"
          >
            <b-form-input
              id="modelNumber"
              v-model="object.modelNumber"
              placeholder="Enter model number"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Site"
            label-for="site"
          >
            <v-select
              id="site"
              v-model="object.siteObjectsId"
              :options="sites"
              label="name"
              placeholder="Select Site"
              :reduce="site => site.id"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>

import Ripple from 'vue-ripple-directive'
import { API } from 'aws-amplify'
import {
  VBModal,
  BCard,
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  VBToggle,
  BButtonGroup,
  BCardHeader,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// import * as queries from '../../graphql/queries'
import { Can } from '@casl/vue'
import vSelect from 'vue-select'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'

export default {
  components: {
    BCard,
    BTable,
    BCardHeader,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BButtonGroup,
    BSpinner,
    vSelect,
    BBadge,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    Can,

  },
  directives: {
    'b-toggle': VBToggle,
    'b-modal': VBModal,
    Ripple,
  },
  data() {
    return {
      isBusy: false,
      nextId: '',
      perPage: 100,
      pageOptions: [10, 25, 50, 100],
      totalRows: 1,
      currentPage: 1,
      sortBy: 'name',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'manufacturerSerialNumber', label: 'Serial Number', sortable: true },
        { key: 'model', label: 'Model', sortable: true },
        { key: 'modelNumber', label: 'Model Number', sortable: true },
        { key: 'manufacturer', label: 'Manufacturer', sortable: true },
        { key: 'site', label: 'Site', sortable: true },
        { key: 'created', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions' },

      ],
      objects: [],
      object: {},
      sitesDict: {},
      sites: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },

  mounted() {
    // Set the initial number of objects
    this.totalRows = this.objects.length
  },
  created() {
    this.listObjects()
    this.listSites()
  },
  methods: {
    triggerToast(title, variant, icon) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      },
      {
        position: 'bottom-right',
      })
    },
    setSite() {
      // set the selected site object when the user selects an option from the v-select component
      if (this.sitesDict[this.object.siteObjectsId]) {
        this.object.siteObjectsId = this.sitesDict[this.object.siteObjectsId].id
      }
    },
    showDeleteModal(data) {
      this.$bvModal.msgBoxConfirm(`Please confirm that you want to delete ${data.item.name}`, {
        title: 'Please Confirm',
        size: 'md',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
        cancelVariant: 'outline-secondary',
        hideHeaderClose: false,
        centered: true,
      })
        .then(value => {
          if (value === true) {
            this.deleteObject(data.item.id)
          }
        })
    },
    async deleteObject(ObjectId) {
      // eslint-disable-next-line no-unused-vars
      const deleteObject = {
        id: ObjectId,
      }
      // eslint-disable-next-line no-unused-vars
      await API.graphql({
        query: mutations.deleteObject,
        variables: {
          input: deleteObject,
        },
      }).then(response => {
        if (response.error) {
          this.triggerToast(`${response.error}`, 'danger', 'DeleteIcon')
        } else {
          this.triggerToast(`${response.data.deleteObject.name} deleted`, 'success', 'DeleteIcon')
          this.listObjects()
        }
      })
    },
    showEditModal(data) {
      this.object = data.item
      this.$refs.editObjectModal.show()
    },
    async updateObject(event) {
      if (this.object.name.trim() === '') {
        this.$refs.objectNameInput.focus() // set focus to name input field
        event.preventDefault() // prevent modal from submitting
      } else {
        const updateObject = {
          id: this.object.id,
          name: this.object.name,
        }

        if (this.object.manufacturerSerialNumber) {
          updateObject.manufacturerSerialNumber = this.object.manufacturerSerialNumber
        } else {
          updateObject.manufacturerSerialNumber = ''
        }

        if (this.object.model) {
          updateObject.model = this.object.model
        } else {
          updateObject.model = ''
        }

        if (this.object.modelNumber) {
          updateObject.modelNumber = this.object.modelNumber
        } else {
          updateObject.modelNumber = ''
        }

        if (this.object.siteObjectsId) {
          updateObject.siteObjectsId = this.object.siteObjectsId
        } else {
          updateObject.siteObjectsId = null
        }

        await API.graphql({
          query: mutations.updateObject,
          variables: {
            input: updateObject,
          },
        }).then(response => {
          if (response.error) {
            this.triggerToast(`${response.error}`, 'danger', 'DeleteIcon')
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-right',
              props: {
                title: `${response.data.updateObject.name} updated`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
            this.listObjects()
          }
        })
      }
    },
    cancelEditObjectModal(event) {
      if (this.object.name.trim() === '') {
        this.$refs.objectNameInput.focus() // set focus to name input field
        event.preventDefault() // prevent modal from submitting
      } else {
        this.$refs.editObjectModal.hide()
      }
    },
    async listObjects() {
      this.isBusy = true
      await API.graphql({
        query: queries.getWorkspace,
        variables: {
          id: JSON.parse(localStorage.getItem('workspace')).id,
        },
      }).then(response => {
        if (response.error) {
          this.triggerToast(`${response.error}`, 'danger', 'DeleteIcon')
        } else {
          this.objects = response.data.getWorkspace.objects.items
          this.isBusy = false
        }
      })
    },
    async listSites() {
      this.isBusy = true
      await API.graphql({
        query: queries.getWorkspace,
        variables: {
          id: JSON.parse(localStorage.getItem('workspace')).id,
        },
      }).then(response => {
        if (response.error) {
          this.triggerToast(`${response.error}`, 'danger', 'DeleteIcon')
        } else {
          this.sites = response.data.getWorkspace.sites.items
          response.data.getWorkspace.sites.items.forEach(site => {
            this.sitesDict[site.id] = site
          })
          this.isBusy = false
        }
      })
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    onRowClick(event) {
      window.location.href = `/object/${event.id}`
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
